import type { MusicLibraryResponse } from '@halo/shared';
import { create } from 'zustand';
import { errorMessage, isAbortError } from '@/api/client';
import { musicApi } from '@/api/endpoints';
import { queryClient } from '@/app/query-client';
import { uploadProblem, type UploadOutcome } from '@/domain/music';
import { registerSessionReset } from '@/stores/session-reset';

export const MUSIC_LIBRARY_KEY = ['music', 'library'] as const;

export interface UploadBatch {
  id: number;
  folderId: string;
  total: number;
  /** Số file đã xử lý xong (đưa lên được + hỏng). */
  index: number;
  /** Tên file đang gửi. */
  fileName: string;
  /** Phần đã gửi của file đang gửi, 0 → 1. */
  fraction: number;
}

export interface FinishedBatch extends UploadOutcome {
  id: number;
  folderId: string;
}

interface MusicUploadState {
  /** Hàng đợi: lô ĐẦU đang chạy, các lô sau chờ tới lượt. */
  batches: UploadBatch[];
  /** Lô vừa kết thúc, chờ giao diện báo cho người dùng rồi `acknowledge`. */
  finished: FinishedBatch[];
  enqueue: (folderId: string, files: File[], maxFileBytes: number) => void;
  /** Lô đang chạy: bỏ file đang gửi dở và các file còn lại. Lô đang chờ: rút khỏi hàng đợi. */
  cancel: (batchId: number) => void;
  acknowledge: (batchId: number) => void;
  reset: () => void;
}

// Phần KHÔNG phải trạng thái hiển thị thì nằm ngoài store: file, tay huỷ, cờ của bộ chạy.
interface Job {
  files: File[];
  maxFileBytes: number;
}
const jobs = new Map<number, Job>();
const cancelled = new Set<number>();
let inFlight: AbortController | null = null;
let draining = false;
let nextId = 1;
/** Tăng mỗi khi phiên kết thúc: bộ chạy của phiên cũ thấy số đã đổi thì dừng, không ghi gì thêm. */
let generation = 0;

/** Đóng thẻ giữa lúc đang đưa lên là mất các file còn lại — để trình duyệt hỏi lại trước. */
const warnBeforeLeaving = (event: BeforeUnloadEvent) => event.preventDefault();
function guardLeaving(active: boolean): void {
  if (typeof window === 'undefined') return;
  if (active) window.addEventListener('beforeunload', warnBeforeLeaving);
  else window.removeEventListener('beforeunload', warnBeforeLeaving);
}

/**
 * HÀNG ĐỢI ĐƯA NHẠC LÊN. Nằm ngoài màn «Kho nhạc» vì một lô vài chục file mất nhiều phút: người dùng
 * sang mục khác làm việc thì lô vẫn chạy tiếp, quay lại vẫn thấy tiến độ (bản cũ làm được vì cả app
 * là một trang). Mỗi lúc chỉ gửi MỘT file — gửi song song chỉ chia nhau cùng một đường truyền, mà
 * hỏng giữa chừng thì khó biết file nào đã lên.
 */
export const useMusicUploadStore = create<MusicUploadState>((set, get) => ({
  batches: [],
  finished: [],

  enqueue(folderId, files, maxFileBytes) {
    if (!files.length) return;
    const id = nextId++;
    jobs.set(id, { files, maxFileBytes });
    const batch: UploadBatch = {
      id,
      folderId,
      total: files.length,
      index: 0,
      fileName: files[0]?.name ?? '',
      fraction: 0,
    };
    set((state) => ({ batches: [...state.batches, batch] }));
    guardLeaving(true);
    void drain();
  },

  cancel(batchId) {
    const running = get().batches[0]?.id === batchId && draining;
    if (running) {
      cancelled.add(batchId);
      inFlight?.abort();
      return;
    }
    jobs.delete(batchId);
    set((state) => ({ batches: state.batches.filter((batch) => batch.id !== batchId) }));
    guardLeaving(get().batches.length > 0);
  },

  acknowledge: (batchId) =>
    set((state) => ({ finished: state.finished.filter((batch) => batch.id !== batchId) })),

  reset() {
    generation += 1;
    inFlight?.abort();
    jobs.clear();
    cancelled.clear();
    guardLeaving(false);
    set({ batches: [], finished: [] });
  },
}));

function patchBatch(batchId: number, change: Partial<UploadBatch>): void {
  useMusicUploadStore.setState((state) => ({
    batches: state.batches.map((batch) => (batch.id === batchId ? { ...batch, ...change } : batch)),
  }));
}

async function drain(): Promise<void> {
  if (draining) return;
  draining = true;
  const startedIn = generation;
  try {
    for (;;) {
      const batch = useMusicUploadStore.getState().batches[0];
      if (!batch || generation !== startedIn) return;
      await runBatch(batch, startedIn);
    }
  } finally {
    draining = false;
    // Phiên đổi giữa chừng mà người mới đã kịp xếp lô: bộ chạy cũ vừa thoát, khởi động lại cho họ.
    if (useMusicUploadStore.getState().batches.length) void drain();
  }
}

async function runBatch(batch: UploadBatch, startedIn: number): Promise<void> {
  const job = jobs.get(batch.id);
  const files = job?.files ?? [];
  const failures: string[] = [];
  let uploaded = 0;

  for (const [position, file] of files.entries()) {
    if (cancelled.has(batch.id)) break;
    patchBatch(batch.id, { index: position, fileName: file.name, fraction: 0 });

    const problem = uploadProblem(file, job?.maxFileBytes ?? 0);
    if (problem) {
      failures.push(`${file.name} (${problem})`);
      continue;
    }

    inFlight = new AbortController();
    let shown = 0;
    try {
      const track = await musicApi.uploadTrack(
        batch.folderId,
        file,
        (fraction) => {
          // Trình duyệt báo tiến độ vài chục lần mỗi giây; vẽ lại theo từng phần trăm là đủ.
          if (fraction < 1 && fraction - shown < 0.01) return;
          shown = fraction;
          patchBatch(batch.id, { fraction });
        },
        inFlight.signal,
      );
      if (generation !== startedIn) return;
      uploaded += 1;
      // Bài vừa lên hiện ngay trong danh sách, không đợi tải lại cả kho.
      queryClient.setQueryData<MusicLibraryResponse>(MUSIC_LIBRARY_KEY, (library) =>
        library ? { ...library, tracks: [...library.tracks, track] } : library,
      );
    } catch (error) {
      if (generation !== startedIn) return;
      if (isAbortError(error)) break;
      failures.push(`${file.name} (${errorMessage(error)})`);
    } finally {
      inFlight = null;
    }
  }

  if (generation !== startedIn) return;
  const outcome: FinishedBatch = {
    id: batch.id,
    folderId: batch.folderId,
    total: files.length,
    uploaded,
    failures,
    cancelled: cancelled.delete(batch.id),
  };
  jobs.delete(batch.id);
  useMusicUploadStore.setState((state) => ({
    batches: state.batches.filter((item) => item.id !== batch.id),
    finished: [...state.finished, outcome],
  }));
  guardLeaving(useMusicUploadStore.getState().batches.length > 0);
  // Đối chiếu lại với máy chủ: thứ tự, dung lượng thật, và cả bài người khác vừa đưa lên.
  void queryClient.invalidateQueries({ queryKey: MUSIC_LIBRARY_KEY });
}

registerSessionReset(() => useMusicUploadStore.getState().reset());
